import React from 'react';
import {
  Users,
  DollarSign,
  Check,
  CalendarDays,
  AlertTriangle,
  XCircle,
  Shield,
  LayoutDashboard,
  Gift,
  Trash2,
  Bug
} from 'lucide-react';
import AdminBugReports from '../components/AdminBugReports';

const AdminDashboard = ({
  user,
  error,
  success,
  adminStats,
  adminUsers,
  adminPagination,
  adminFilters,
  setAdminFilters,
  adminLoading,
  selectedAdminUser,
  setSelectedAdminUser,
  setCurrentView,
  loadAdminStats,
  loadAdminUsers,
  loadAdminUserDetail,
  handleAdminUpdatePlan,
  handleAdminDeleteUser,
  handleAdminDeactivateUser,
  handleLogout,
  API_URL
}) => {
  return (
    <div className="app-container">
      <nav className="navbar">
        <div className="nav-brand" onClick={() => setCurrentView('admin')}>
          <img src="/plate-planner-pro-logo.webp" alt="Logo" className="nav-logo-img" />
          <span>Plate Planner Pro</span>
          <span className="admin-indicator">Admin</span>
        </div>
        <div className="nav-links">
          <button className="nav-link admin-link" onClick={() => { setCurrentView('admin'); loadAdminStats(); loadAdminUsers(); }}><LayoutDashboard size={16} /> Dashboard</button>
        </div>
        <div className="nav-user">
          <span className="user-name">{user.name}</span>
          <span className="admin-badge">Admin</span>
          <button className="btn-logout" onClick={handleLogout}>Logout</button>
        </div>
      </nav>

      <main className="main-content">
        {error && <div className="alert alert-error" style={{marginBottom: '16px'}}>{error}</div>}
        {success && <div className="alert alert-success" style={{marginBottom: '16px'}}>{success}</div>}

        {/* Admin Dashboard View */}
        <div className="admin-view">
          <div className="page-header">
            <h2><Shield size={24} style={{marginRight: '8px'}} />Admin Dashboard</h2>
          </div>

          {/* Stats Overview */}
          {adminStats && (
            <div className="admin-stats-grid">
              <div 
                className={`admin-stat-card clickable ${adminFilters.status === '' && adminFilters.plan === '' ? 'active-filter' : ''}`}
                onClick={() => { const newFilters = {plan: '', status: '', search: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                title="Show all users"
              >
                <div className="stat-icon"><Users size={20} /></div>
                <div className="stat-content">
                  <span className="stat-value">{adminStats.total_users}</span>
                  <span className="stat-label">Total Users</span>
                </div>
              </div>
              <div className="admin-stat-card highlight">
                <div className="stat-icon"><DollarSign size={20} /></div>
                <div className="stat-content">
                  <span className="stat-value">£{adminStats.estimated_mrr}</span>
                  <span className="stat-label">Est. MRR</span>
                </div>
              </div>
              <div 
                className={`admin-stat-card clickable ${adminFilters.status === 'active' ? 'active-filter' : ''}`}
                onClick={() => { const newFilters = {...adminFilters, status: 'active', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                title="Filter: Active paying users"
              >
                <div className="stat-icon"><Check size={20} /></div>
                <div className="stat-content">
                  <span className="stat-value">{adminStats.active_paying_users}</span>
                  <span className="stat-label">Active Paying</span>
                </div>
              </div>
              <div 
                className={`admin-stat-card clickable ${adminFilters.status === 'trialing' ? 'active-filter' : ''}`}
                onClick={() => { const newFilters = {...adminFilters, status: 'trialing', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                title="Filter: Users on trial"
              >
                <div className="stat-icon"><CalendarDays size={20} /></div>
                <div className="stat-content">
                  <span className="stat-value">{adminStats.users_by_status.trialing || 0}</span>
                  <span className="stat-label">On Trial</span>
                </div>
              </div>
              <div 
                className={`admin-stat-card clickable ${adminFilters.status === 'past_due' ? 'active-filter' : ''}`}
                onClick={() => { const newFilters = {...adminFilters, status: 'past_due', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                title="Filter: Payment failed"
              >
                <div className="stat-icon"><AlertTriangle size={20} /></div>
                <div className="stat-content">
                  <span className="stat-value">{adminStats.users_by_status.past_due || 0}</span>
                  <span className="stat-label">Past Due</span>
                </div>
              </div>
              <div 
                className={`admin-stat-card clickable ${adminFilters.status === 'canceled' ? 'active-filter' : ''}`}
                onClick={() => { const newFilters = {...adminFilters, status: 'canceled', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                title="Filter: Canceled users"
              >
                <div className="stat-icon"><XCircle size={20} /></div>
                <div className="stat-content">
                  <span className="stat-value">{adminStats.users_by_status.canceled || 0}</span>
                  <span className="stat-label">Canceled</span>
                </div>
              </div>
            </div>
          )}

          {/* Plans Breakdown */}
          {adminStats && (
            <div className="admin-section">
              <h3>Users by Plan <span className="section-hint">(click to filter)</span></h3>
              <div className="plan-breakdown">
                <div 
                  className={`plan-bar-item clickable ${adminFilters.plan === 'starter' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, plan: 'starter', status: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  <span className="plan-name">Starter</span>
                  <div className="plan-bar">
                    <div 
                      className="plan-bar-fill starter" 
                      style={{width: `${adminStats.total_users > 0 ? (adminStats.users_by_plan.starter / adminStats.total_users) * 100 : 0}%`}}
                    />
                  </div>
                  <span className="plan-count">{adminStats.users_by_plan.starter}</span>
                </div>
                <div 
                  className={`plan-bar-item clickable ${adminFilters.plan === 'pro' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, plan: 'pro', status: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  <span className="plan-name">Pro</span>
                  <div className="plan-bar">
                    <div 
                      className="plan-bar-fill pro" 
                      style={{width: `${adminStats.total_users > 0 ? (adminStats.users_by_plan.pro / adminStats.total_users) * 100 : 0}%`}}
                    />
                  </div>
                  <span className="plan-count">{adminStats.users_by_plan.pro}</span>
                </div>
                <div 
                  className={`plan-bar-item clickable ${adminFilters.plan === 'unlimited' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, plan: 'unlimited', status: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  <span className="plan-name">Unlimited</span>
                  <div className="plan-bar">
                    <div 
                      className="plan-bar-fill unlimited" 
                      style={{width: `${adminStats.total_users > 0 ? (adminStats.users_by_plan.unlimited / adminStats.total_users) * 100 : 0}%`}}
                    />
                  </div>
                  <span className="plan-count">{adminStats.users_by_plan.unlimited}</span>
                </div>
              </div>
            </div>
          )}

          {/* Status Breakdown */}
          {adminStats && (
            <div className="admin-section">
              <h3>Users by Status <span className="section-hint">(click to filter)</span></h3>
              <div className="status-pills">
                <span 
                  className={`status-pill trialing clickable ${adminFilters.status === 'trialing' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'trialing', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  Trialing: {adminStats.users_by_status.trialing || 0}
                </span>
                <span 
                  className={`status-pill active clickable ${adminFilters.status === 'active' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'active', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  Active: {adminStats.users_by_status.active || 0}
                </span>
                <span 
                  className={`status-pill expired clickable ${adminFilters.status === 'expired' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'expired', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  Expired: {adminStats.users_by_status.expired || 0}
                </span>
                <span 
                  className={`status-pill canceled clickable ${adminFilters.status === 'canceled' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'canceled', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  Canceled: {adminStats.users_by_status.canceled || 0}
                </span>
              </div>
            </div>
          )}

          {/* User Management */}
          <div className="admin-section">
            <h3>User Management</h3>
            
            {/* Active Filter Indicator */}
            {(adminFilters.plan || adminFilters.status || adminFilters.search) && (
              <div className="active-filter-indicator">
                <span>
                  Filtering: {adminFilters.plan && `Plan: ${adminFilters.plan}`} {adminFilters.status && `Status: ${adminFilters.status}`} {adminFilters.search && `Search: "${adminFilters.search}"`}
                </span>
                <button 
                  className="clear-filter-btn"
                  onClick={() => { const newFilters = {plan: '', status: '', search: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                >
                  ✕ Clear filters
                </button>
              </div>
            )}
            
            {/* Filters */}
            <div className="admin-filters">
              <input
                type="text"
                placeholder="Search by email or name..."
                value={adminFilters.search}
                onChange={(e) => setAdminFilters({...adminFilters, search: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && loadAdminUsers(1)}
              />
              <select
                value={adminFilters.plan}
                onChange={(e) => {
                  const newFilters = {...adminFilters, plan: e.target.value};
                  setAdminFilters(newFilters);
                  loadAdminUsers(1, newFilters);
                }}
              >
                <option value="">All Plans</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="unlimited">Unlimited</option>
              </select>
              <select
                value={adminFilters.status}
                onChange={(e) => {
                  const newFilters = {...adminFilters, status: e.target.value};
                  setAdminFilters(newFilters);
                  loadAdminUsers(1, newFilters);
                }}
              >
                <option value="">All Status</option>
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="expired">Expired</option>
                <option value="canceled">Canceled</option>
              </select>
              <button className="btn-primary btn-sm" onClick={() => loadAdminUsers(1)}>
                Search
              </button>
            </div>

            {/* Users Table */}
            {adminLoading ? (
              <div className="loading-state">Loading users...</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Clients</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map(u => (
                      <tr key={u.user_id} className={u.is_admin ? 'admin-row' : ''}>
                        <td>
                          <div className="user-cell">
                            <span className="user-name">{u.name}</span>
                            <span className="user-email">{u.email}</span>
                            {u.is_admin && <span className="admin-badge">Admin</span>}
                            {!u.email_verified && <span className="unverified-badge">Unverified</span>}
                          </div>
                        </td>
                        <td>
                          <span className={`plan-badge plan-${u.plan}`}>{u.plan}</span>
                        </td>
                        <td>
                          <span className={`status-badge status-${u.plan_status}`}>{u.plan_status}</span>
                        </td>
                        <td>{u.clients_count || 0}</td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-sm btn-secondary"
                              onClick={() => loadAdminUserDetail(u.user_id)}
                            >
                              View
                            </button>
                            {!u.is_admin && (
                              <button 
                                className="btn-sm btn-danger"
                                onClick={() => handleAdminDeleteUser(u.user_id)}
                                title="Delete user"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {adminPagination.total_pages > 1 && (
              <div className="admin-pagination">
                <button 
                  className="btn-sm"
                  disabled={adminPagination.page === 1}
                  onClick={() => loadAdminUsers(adminPagination.page - 1)}
                >
                  Previous
                </button>
                <span>Page {adminPagination.page} of {adminPagination.total_pages}</span>
                <button 
                  className="btn-sm"
                  disabled={adminPagination.page === adminPagination.total_pages}
                  onClick={() => loadAdminUsers(adminPagination.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Bug Reports Section */}
          <AdminBugReports API_URL={API_URL} />

          {/* User Detail Modal */}
          {selectedAdminUser && (
            <div className="modal-overlay" onClick={() => setSelectedAdminUser(null)}>
              <div className="admin-user-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>User Details</h3>
                  <button className="close-btn" onClick={() => setSelectedAdminUser(null)}>×</button>
                </div>
                
                <div className="user-detail-content">
                  <div className="user-detail-header">
                    <div>
                      <h4>{selectedAdminUser.name}</h4>
                      <p>{selectedAdminUser.email}</p>
                      {selectedAdminUser.is_admin && <span className="admin-badge">Admin</span>}
                    </div>
                    <div className="user-plan-status">
                      <span className={`plan-badge plan-${selectedAdminUser.plan}`}>{selectedAdminUser.plan}</span>
                      <span className={`status-badge status-${selectedAdminUser.plan_status}`}>{selectedAdminUser.plan_status}</span>
                    </div>
                  </div>

                  <div className="user-stats-row">
                    <div className="user-stat">
                      <span className="label">Clients</span>
                      <span className="value">{selectedAdminUser.clients_count || 0}</span>
                    </div>
                    <div className="user-stat">
                      <span className="label">Meal Plans</span>
                      <span className="value">{selectedAdminUser.meal_plans_count || 0}</span>
                    </div>
                    <div className="user-stat">
                      <span className="label">Joined</span>
                      <span className="value">{new Date(selectedAdminUser.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="user-stat">
                      <span className="label">Email Verified</span>
                      <span className="value">{selectedAdminUser.email_verified ? <><Check size={14} /> Yes</> : <><XCircle size={14} /> No</>}</span>
                    </div>
                  </div>

                  {/* Subscription Management */}
                  <div className="admin-action-section">
                    <h5>Subscription Management</h5>
                    <div className="subscription-actions">
                      <button 
                        className={`btn-sm ${selectedAdminUser.plan_status === 'active' ? 'btn-warning' : 'btn-secondary'}`}
                        onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, selectedAdminUser.plan, 'past_due')}
                        disabled={selectedAdminUser.plan_status === 'past_due'}
                      >
                        ⏸️ Pause Subscription
                      </button>
                      <button 
                        className={`btn-sm ${selectedAdminUser.plan_status !== 'canceled' ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, selectedAdminUser.plan, 'canceled')}
                        disabled={selectedAdminUser.plan_status === 'canceled'}
                      >
                        <XCircle size={14} /> Cancel Subscription
                      </button>
                      <button 
                        className={`btn-sm btn-success`}
                        onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, selectedAdminUser.plan, 'active')}
                        disabled={selectedAdminUser.plan_status === 'active'}
                      >
                        <Check size={14} /> Reactivate
                      </button>
                    </div>
                  </div>

                  {/* Plan Management */}
                  <div className="admin-action-section">
                    <h5><Gift size={16} style={{marginRight: '6px'}} />Gift Free Plan Upgrade</h5>
                    <p className="admin-hint">Upgrade users without charging them - bypasses Stripe</p>
                    <div className="plan-change-buttons">
                      <button 
                        className={`btn-sm ${selectedAdminUser.plan === 'starter' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, 'starter', 'active')}
                      >
                        <Gift size={12} /> Gift Starter
                      </button>
                      <button 
                        className={`btn-sm ${selectedAdminUser.plan === 'pro' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, 'pro', 'active')}
                      >
                        <Gift size={12} /> Gift Pro
                      </button>
                      <button 
                        className={`btn-sm ${selectedAdminUser.plan === 'unlimited' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, 'unlimited', 'active')}
                      >
                        <Gift size={12} /> Gift Unlimited
                      </button>
                    </div>
                  </div>

                  {/* Recent Clients */}
                  {selectedAdminUser.recent_clients?.length > 0 && (
                    <div className="admin-action-section">
                      <h5>Recent Clients</h5>
                      <ul className="recent-list">
                        {selectedAdminUser.recent_clients.map(c => (
                          <li key={c.client_id}>{c.first_name} {c.last_name} - {c.email}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Payment History */}
                  {selectedAdminUser.payment_history?.length > 0 && (
                    <div className="admin-action-section">
                      <h5>Payment History</h5>
                      <ul className="recent-list">
                        {selectedAdminUser.payment_history.map((p, idx) => (
                          <li key={idx}>
                            £{p.amount} - {p.plan} - {p.payment_status} - {new Date(p.created_at).toLocaleDateString()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Danger Zone */}
                  <div className="admin-action-section danger-zone">
                    <h5>Danger Zone</h5>
                    <button 
                      className={`btn-sm btn-with-icon ${selectedAdminUser.is_deactivated ? 'btn-primary' : 'btn-warning'}`}
                      data-testid="admin-deactivate-btn"
                      onClick={() => handleAdminDeactivateUser(selectedAdminUser.user_id)}
                    >
                      {selectedAdminUser.is_deactivated ? (
                        <><Check size={14} /> Reactivate User</>
                      ) : (
                        <><XCircle size={14} /> Deactivate User</>
                      )}
                    </button>
                    <button 
                      className="btn-danger btn-sm btn-with-icon"
                      onClick={() => handleAdminDeleteUser(selectedAdminUser.user_id)}
                    >
                      <Trash2 size={14} /> Delete User & All Data
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <p>© 2026 Plate Planner Pro - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminDashboard;
